// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Group, Paper, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { DataTable } from 'mantine-datatable';
import type { JSX } from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { TableAnchor } from '../../components/layout/TableAnchor';
import { NewTopicDialog } from '../../components/messages/NewTopicDialog';
import type { ActiveCoverageEntry } from '../../hooks/useActiveCoverage';
import { useActiveCoverage } from '../../hooks/useActiveCoverage';
import type { IneligibleInsuranceEntry } from '../../hooks/useIneligibleInsurance';
import { useIneligibleInsurance } from '../../hooks/useIneligibleInsurance';
import { EligibilityDetailDrawer } from './EligibilityDetailDrawer';

const PAGE_SIZE = 20;

function formatDateTime(iso: string | undefined): string {
  if (!iso) {
    return '—';
  }
  return new Date(iso).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function ActiveCoverageSection(): JSX.Element {
  const [page, setPage] = useState(1);
  const [patientName, setPatientName] = useState('');
  const [eligibility, setEligibility] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedCerRef, setSelectedCerRef] = useState<string | null>(null);
  const [drawerOpened, drawerHandlers] = useDisclosure(false);

  const { entries, loading, total } = useActiveCoverage({
    page,
    pageSize: PAGE_SIZE,
    filters: { patientName, eligibility: eligibility ?? undefined, search },
  });

  return (
    <>
      <Paper shadow="sm" withBorder radius="md" bg="white" p={0}>
        <Stack gap="md" p="md">
          <Group justify="space-between" align="flex-end">
            <Title order={3} fw={700} size="1.5rem">
              Active Coverage
            </Title>
            <Group gap="sm" align="flex-end">
              <TextInput
                label="Patient"
                placeholder="Patient"
                value={patientName}
                size="sm"
                w={155}
                onChange={(e) => {
                  setPatientName(e.currentTarget.value);
                  setPage(1);
                }}
              />
              <Select
                label="Eligibility"
                placeholder="Eligibility"
                data={['New', 'Verified', 'Re-check']}
                value={eligibility}
                size="sm"
                w={155}
                clearable
                onChange={(val) => {
                  setEligibility(val);
                  setPage(1);
                }}
              />
              <Select label="Encounter Type" placeholder="—" data={[]} disabled size="sm" w={175} />
              <TextInput
                label="Search"
                placeholder="Search"
                value={search}
                size="sm"
                w={155}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  setPage(1);
                }}
              />
            </Group>
          </Group>

          <DataTable
            fetching={loading}
            minHeight={200}
            records={entries}
            idAccessor="cerRef"
            striped
            highlightOnHover
            fz="sm"
            noRecordsText="No active coverage found."
            totalRecords={total}
            recordsPerPage={PAGE_SIZE}
            page={page}
            onPageChange={setPage}
            columns={[
              {
                accessor: 'patientName',
                title: 'Patient',
                render: ({ patientId, patientName: name }: ActiveCoverageEntry) => (
                  <TableAnchor component={Link} to={`/Patient/${patientId}`} size="sm">
                    {name}
                  </TableAnchor>
                ),
              },
              {
                accessor: 'dateChecked',
                title: 'Date Checked',
                render: ({ dateChecked }: ActiveCoverageEntry) => formatDateTime(dateChecked),
              },
              { accessor: 'encounterType', title: 'Encounter Type' },
              { accessor: 'payorId', title: 'Payor ID' },
              { accessor: 'deductibleYTD', title: 'Deductible YTD' },
              { accessor: 'oopMax', title: 'OoP Max' },
              { accessor: 'copay', title: 'Copay' },
              {
                accessor: 'actions',
                title: 'Actions',
                render: ({ cerRef }: ActiveCoverageEntry) => (
                  <TableAnchor
                    component="button"
                    type="button"
                    size="sm"
                    onClick={() => {
                      setSelectedCerRef(cerRef);
                      drawerHandlers.open();
                    }}
                  >
                    View Details
                  </TableAnchor>
                ),
              },
            ]}
          />
        </Stack>
      </Paper>
      <EligibilityDetailDrawer cerRef={selectedCerRef} opened={drawerOpened} onClose={drawerHandlers.close} />
    </>
  );
}

function IneligibleInsuranceSection(): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [contactOpened, contactHandlers] = useDisclosure(false);
  const [contactPatientId, setContactPatientId] = useState<string | null>(null);

  const { entries, loading, total } = useIneligibleInsurance({ page, pageSize: PAGE_SIZE, search });

  return (
    <>
      <Paper shadow="sm" withBorder radius="md" bg="white" p={0}>
        <Stack gap="md" p="md">
          <Group justify="space-between" align="center">
            <Title order={3} fw={700} size="1.5rem">
              Ineligible Due to Insurance
            </Title>
            <TextInput
              placeholder="Search"
              value={search}
              size="sm"
              w={200}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
                setPage(1);
              }}
            />
          </Group>

          <DataTable
            fetching={loading}
            minHeight={200}
            records={entries}
            idAccessor="cerRef"
            striped
            highlightOnHover
            fz="sm"
            noRecordsText="No ineligible patients found."
            totalRecords={total}
            recordsPerPage={PAGE_SIZE}
            page={page}
            onPageChange={setPage}
            columns={[
              {
                accessor: 'patientName',
                title: 'Patient',
                render: ({ patientId, patientName: name }: IneligibleInsuranceEntry) => (
                  <TableAnchor component={Link} to={`/Patient/${patientId}`} size="sm">
                    {name}
                  </TableAnchor>
                ),
              },
              {
                accessor: 'dateChecked',
                title: 'Date Checked',
                render: ({ dateChecked }: IneligibleInsuranceEntry) => formatDateTime(dateChecked),
              },
              { accessor: 'status', title: 'Status' },
              { accessor: 'errorCode', title: 'Error Code' },
              { accessor: 'requestedSTC', title: 'Requested STC' },
              { accessor: 'payorId', title: 'Payor ID' },
              {
                accessor: 'nextEncounterDate',
                title: 'Next Encounter',
                render: ({ patientId, nextEncounterDate }: IneligibleInsuranceEntry) =>
                  nextEncounterDate ? (
                    <TableAnchor component={Link} to={`/Patient/${patientId}`} size="sm">
                      {new Date(nextEncounterDate).toLocaleDateString('en-US')}
                    </TableAnchor>
                  ) : (
                    <Text size="sm">—</Text>
                  ),
              },
              {
                accessor: 'actions',
                title: 'Actions',
                render: ({ patientId, compositionId }: IneligibleInsuranceEntry) => (
                  <Group gap="xs" wrap="nowrap">
                    {compositionId && (
                      <TableAnchor component={Link} to={`/triage/composition/${compositionId}`} size="sm">
                        View Screener
                      </TableAnchor>
                    )}
                    <TableAnchor component={Link} to={`/Patient/${patientId}`} size="sm">
                      View Chart
                    </TableAnchor>
                    <TableAnchor
                      component="button"
                      type="button"
                      size="sm"
                      onClick={() => {
                        setContactPatientId(patientId);
                        contactHandlers.open();
                      }}
                    >
                      Contact
                    </TableAnchor>
                  </Group>
                ),
              },
            ]}
          />
        </Stack>
      </Paper>
      <NewTopicDialog
        opened={contactOpened}
        subject={contactPatientId ? { reference: `Patient/${contactPatientId}` } : undefined}
        onClose={() => {
          contactHandlers.close();
          setContactPatientId(null);
        }}
        onSubmit={(thread) => {
          if (contactPatientId) {
            navigate(`/Patient/${contactPatientId}/Communication/${thread.id}`);
          }
        }}
      />
    </>
  );
}

export function EligibilityTab(): JSX.Element {
  return (
    <Stack gap="lg">
      <ActiveCoverageSection />
      <IneligibleInsuranceSection />
    </Stack>
  );
}
